import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import SiteLayout from "@/components/SiteLayout";
import track from "@/lib/track";

function DeckDownload() {
  useEffect(() => {
    track("pageview", "/deck");
    track("deck_opened", "/deck");
    window.location.replace("/decks/deck.html");
  }, []);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "sans-serif", color: "#475569" }}>
      Opening the JCA Matrix client deck…&nbsp;
      <a href="/decks/deck.html" style={{ color: "#2563eb" }} data-testid="deck-download-link">click here</a>&nbsp;if it doesn't open.
    </div>
  );
}
import Home from "@/pages/site/Home";
import WhoWeServe from "@/pages/site/WhoWeServe";
import Approach from "@/pages/site/Approach";
import Engagements from "@/pages/site/Engagements";
import Deliverables from "@/pages/site/Deliverables";
import Trust from "@/pages/site/Trust";
import Contact from "@/pages/site/Contact";
import { Privacy, Terms } from "@/pages/site/Legal";
import Presentation from "@/pages/site/Presentation";
import TeamLogin from "@/pages/TeamLogin";

const AppShell = lazy(() => import("@/components/AppShell"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const DecisionCentre = lazy(() => import("@/pages/DecisionCentre"));
const EvidenceGraph = lazy(() => import("@/pages/EvidenceGraph"));
const Assets = lazy(() => import("@/pages/Assets"));
const AssetDetail = lazy(() => import("@/pages/AssetDetail"));
const Documents = lazy(() => import("@/pages/Documents"));
const ExecutiveReports = lazy(() => import("@/pages/ExecutiveReports"));
const ActionPlan = lazy(() => import("@/pages/ActionPlan"));
const ProjectWorkspace = lazy(() => import("@/pages/ProjectWorkspace"));
const Report = lazy(() => import("@/pages/Report"));
const ComparatorStrategy = lazy(() => import("@/pages/ComparatorStrategy"));
const PicoIntelligence = lazy(() => import("@/pages/PicoIntelligence"));
const PicoTraceability = lazy(() => import("@/pages/PicoTraceability"));
const PicoCoverage = lazy(() => import("@/pages/PicoCoverage"));
const EvidenceOpportunity = lazy(() => import("@/pages/EvidenceOpportunity"));
const Defensibility = lazy(() => import("@/pages/Defensibility"));
const FaceValidity = lazy(() => import("@/pages/FaceValidity"));
const MethodRisk = lazy(() => import("@/pages/MethodRisk"));
const EvidenceStrategy = lazy(() => import("@/pages/EvidenceStrategy"));
const LessonsHub = lazy(() => import("@/pages/LessonsHub"));
const LearningUpdates = lazy(() => import("@/pages/LearningUpdates"));
const Outreach = lazy(() => import("@/pages/Outreach"));
const AssumptionRegister = lazy(() => import("@/pages/AssumptionRegister"));
const ItcEngine = lazy(() => import("@/pages/ItcEngine"));
const EvidencePlanner = lazy(() => import("@/pages/EvidencePlanner"));
const PatientEvidence = lazy(() => import("@/pages/PatientEvidence"));
const RweStrategy = lazy(() => import("@/pages/RweStrategy"));
const SubmissionReadiness = lazy(() => import("@/pages/SubmissionReadiness"));
const MarketAccessReadiness = lazy(() => import("@/pages/MarketAccessReadiness"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const SiteAnalytics = lazy(() => import("@/pages/SiteAnalytics"));
const KnowledgeCentre = lazy(() => import("@/pages/KnowledgeCentre"));
const Placeholder = lazy(() => import("@/pages/Placeholder"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AppRouter() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 text-blue-600 animate-spin" /></div>}>
    <Routes>
      <Route path="/presentation" element={<Presentation />} />
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/who-we-serve" element={<WhoWeServe />} />
        <Route path="/approach" element={<Approach />} />
        <Route path="/engagements" element={<Engagements />} />
        <Route path="/deliverables" element={<Deliverables />} />
        <Route path="/trust" element={<Trust />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
      </Route>
      <Route path="/login" element={<TeamLogin />} />
      <Route path="/deck" element={<DeckDownload />} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/decision-centre" element={<DecisionCentre />} />
        <Route path="/evidence-graph" element={<EvidenceGraph />} />
        <Route path="/assets" element={<Assets />} />
        <Route path="/assets/:assetId" element={<AssetDetail />} />
        <Route path="/assets/:assetId/report" element={<Report />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/reports" element={<ExecutiveReports />} />
        <Route path="/assets/:assetId/plan/:planKey" element={<ActionPlan />} />
        <Route path="/module/comparator" element={<ComparatorStrategy />} />
        <Route path="/module/pico-intel" element={<PicoIntelligence />} />
        <Route path="/module/evidence-opportunity" element={<EvidenceOpportunity />} />
        <Route path="/module/defensibility" element={<Defensibility />} />
        <Route path="/module/face-validity" element={<FaceValidity />} />
        <Route path="/module/method-risk" element={<MethodRisk />} />
        <Route path="/module/evidence-strategy" element={<EvidenceStrategy />} />
        <Route path="/assumptions" element={<AssumptionRegister />} />
        <Route path="/module/pico-trace" element={<PicoTraceability />} />
        <Route path="/module/pico-coverage" element={<PicoCoverage />} />
        <Route path="/module/itc" element={<ItcEngine />} />
        <Route path="/module/planner" element={<EvidencePlanner />} />
        <Route path="/module/patient" element={<PatientEvidence />} />
        <Route path="/module/rwe" element={<RweStrategy />} />
        <Route path="/module/dossier" element={<SubmissionReadiness />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/site-analytics" element={<SiteAnalytics />} />
        <Route path="/knowledge" element={<KnowledgeCentre />} />
        <Route path="/lessons" element={<LessonsHub />} />
        <Route path="/learning-updates" element={<LearningUpdates />} />
        <Route path="/outreach" element={<Outreach />} />
        <Route path="/workspace" element={<ProjectWorkspace />} />
        <Route path="/module/:key" element={<Placeholder />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
